<template>
  <b-row class="justify-content-center">
    <b-col md="10">
      <div class="profile__card">
        <div class="d-flex px-u8 py-u8">
          <BaseAvatar
            :name="`${user.firstname} ${user.lastname}`"
            :img="user.profile_picture != null ? user.profile_picture.url : null"
          />

          <div class="ml-u8">
            <div class="profile__name font-weight-bold">
              {{ user.firstname }} {{ user.lastname }}
            </div>
            <div class="profile__email text-dark">{{ user.email }}</div>

            <BaseHideContentWrapper :active="isDescriptionHidden">
              <div class="profile__description mt-u3">
                {{ user.description }}
              </div>
            </BaseHideContentWrapper>
          </div>

          <div v-if="editable" class="ml-auto">
            <b-button variant="outline-primary" @click="goToEditProfile"> Edit </b-button>
          </div>
        </div>

        <div class="basic-info__card px-u8 pt-u5 pb-u8">
          <div class="d-flex justify-content-between align-items-center">
            <div class="h5 font-weight-bold m-0">Basic Infomation</div>
            <b-button v-if="editable" variant="outline-primary" @click="goToEditBasicInfo">
              Edit
            </b-button>
          </div>

          <div class="basic-info__title mt-u6">Date of birth</div>
          <div>{{ dateOfBirth }}</div>

          <div class="basic-info__title mt-u6">Current occupation</div>
          <div>{{ currentOccupation }}</div>

          <div class="basic-info__title mt-u6">Desired occupation</div>
          <div>{{ desiredOccupation }}</div>

          <div class="basic-info__title mt-u6">Area of employment</div>
          <div>{{ areaOfEmployment }}</div>

          <div class="basic-info__title mt-u6">Experience level</div>
          <div>{{ experienceLevel }}</div>

          <div class="basic-info__title mt-u6">Highest level of education</div>
          <div>{{ educationLevel }}</div>

          <div class="basic-info__title mt-u6">Goals</div>
          <div>{{ goals }}</div>

          <div class="basic-info__title mt-u6">Course type</div>
          <div v-if="user.like_to_learn && user.like_to_learn.length > 0">
            <div v-for="(courseType, index) in user.like_to_learn" :key="index">
              <span>
                {{ courseType }}
              </span>
              <span class="ml-u2 text-description">
                {{ descriptionCourseTypes[courseType] }}
              </span>
            </div>
          </div>
          <div v-else>-</div>

          <div class="basic-info__title mt-u6">Interest</div>
          <div>{{ interests }}</div>
        </div>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import moment from 'moment';
import BaseAvatar from '@/components/base-components/BaseAvatar.vue';
import BaseHideContentWrapper from '@/components/base-components/BaseHideContentWrapper.vue';
import * as PROFILE_OPTIONS from '@/assets/user-profile-template.json';

export default {
  components: {
    BaseAvatar,
    BaseHideContentWrapper
  },

  props: {
    editable: {
      type: Boolean,
      default: false
    },

    user: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      descriptionCourseTypes: {
        'Short courses': '1-4 weeks. Build new skills quickly over a few weeks',
        ExpertTracks: '6-21 weeks. Take a series of specialist courses in your own time',
        Microcredentials: '10-15 weeks. Earn accreditation with in-depth, career-focussed study',
        'Online degrees': '12+ weeks. Complete a full degree course flexibly, 100% online'
      },

      isDescriptionHidden: true
    };
  },

  computed: {
    dateOfBirth() {
      return this.user.date_of_birth ? moment(this.user.date_of_birth).format('D MMM YYYY') : '-';
    },

    currentOccupation() {
      return this.user?.current_occupation
        ? this.currentOccupations[this.user.current_occupation]
        : '-';
    },

    desiredOccupation() {
      return this.user?.desired_occupation
        ? this.desiredOccupations[this.user.desired_occupation]
        : '-';
    },

    areaOfEmployment() {
      const level = this.user?.area_of_employment;

      if (level) {
        if (level.includes('Other')) {
          return level.split('|')[1];
        } else {
          return this.employmentAreas[this.user.area_of_employment];
        }
      } else {
        return '-';
      }
    },

    experienceLevel() {
      return this.user?.experience_level ? this.experienceLevels[this.user.experience_level] : '-';
    },

    educationLevel() {
      const level = this.user?.education_level;

      if (level) {
        if (level.includes('Other')) {
          return level.split('|')[1];
        } else {
          return level;
        }
      } else {
        return '-';
      }
    },

    goals() {
      return this.user?.goals && this.user?.goals.length > 0
        ? [...this.user.goals].join(', ')
        : '-';
    },

    interests() {
      let interests = [];

      if (this.user?.interested_micro_credentials?.length) {
        interests = [...this.user.interested_micro_credentials];
      }

      if (interests && interests.length > 0) {
        const otherIndex = interests.findIndex((str) => str.includes('Other'));

        if (otherIndex !== -1) {
          const splittedOtherInterest = interests.splice(otherIndex, 1)[0];

          const splittedOtherText = splittedOtherInterest.split('|')[1];

          interests.push(splittedOtherText);
        }

        return interests.join(', ');
      } else {
        return '-';
      }
    },

    currentOccupations() {
      const results = {};
      PROFILE_OPTIONS.aboutYouFields.fields.currentOccupation.options.forEach((option) => {
        results[option.value] = option.name;
      });
      return results;
    },

    desiredOccupations() {
      const results = {};
      PROFILE_OPTIONS.aboutYouFields.fields.desiredOccupation.options.forEach((option) => {
        results[option.value] = option.name;
      });
      return results;
    },

    employmentAreas() {
      const results = {};
      PROFILE_OPTIONS.aboutYouFields.fields.employmentArea.options.forEach((option) => {
        results[option.value] = option.name;
      });
      return results;
    },

    experienceLevels() {
      const results = {};
      PROFILE_OPTIONS.aboutYouFields.fields.experienceLevel.options.forEach((option) => {
        results[option.value] = option.name;
      });
      return results;
    }
  },

  methods: {
    goToEditProfile() {
      return this.$router.push({ name: 'profile/edit' });
    },

    goToEditBasicInfo() {
      return this.$router.push({ name: 'profile/basic-info/edit' });
    }
  }
};
</script>

<style scoped>
.profile__card {
  border: 1px solid rgba(7, 9, 25, 0.125);
  border-radius: 16px;
}

.profile__avatar {
  width: 100px;
  height: 100px;
}

.profile__name {
  font-size: 28px;
  line-height: 30px;
  text-transform: capitalize;
}

.profile__email {
  font-size: 15.2px;
}

.basic-info__card {
  border-top: 1px solid rgba(7, 9, 25, 0.125);
}

.basic-info__title {
  opacity: 0.5;
}

.avatar {
  border-radius: 50%;
  text-align: center;
  text-transform: uppercase;
  font-size: 32px;
  line-height: 100px;
}
</style>
